import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import Home from "@/pages/Home";
const Privacy = lazy(() => import("@/pages/Privacy"));
const LegalNotice = lazy(() => import("@/pages/LegalNotice"));
const Conditions = lazy(() => import("@/pages/Conditions"));
import Verify from "@/pages/Verify";
import AuthGoogle from "@/pages/AuthGoogle";
import PhoneGate from "@/components/PhoneGate";
import CookieBanner from "@/components/CookieBanner";

const AppShell = lazy(() => import("@/pages/app/AppShell"));
const ParcoursPage = lazy(() => import("@/pages/app/ParcoursPage"));
const FichePage = lazy(() => import("@/pages/app/FichePage"));
const SettingsPage = lazy(() => import("@/pages/app/SettingsPage"));
const SessionPage = lazy(() => import("@/pages/app/SessionPage"));
const AdminPage = lazy(() => import("@/pages/admin/AdminPage"));
const ApercusPage = lazy(() => import("@/pages/admin/ApercusPage"));
const PROD_HOST = /(^|\.)archijo\.com$|\.emergent\.host$/.test(window.location.hostname);


function KeepQuery({ to, add }) {
  // Old routes (/app/quetes, /app/jo) -> Parcours, keeping ?q= / ?write= so the panel opens.
  const { search } = useLocation();
  const p = new URLSearchParams(search);
  if (add) add.split("&").forEach((kv) => p.set(...kv.split("=")));
  const s = p.toString();
  return <Navigate to={`${to}${s ? `?${s}` : ""}`} replace />;
}

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-dvh bg-bg" />;
  if (!user) return <Navigate to="/" replace />;
  return <Suspense fallback={<div className="min-h-dvh bg-bg" />}>{children}</Suspense>;
}

function Gate({ children }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (user?.needs_phone && pathname !== "/confidentialite" && !pathname.startsWith("/admin")) return <PhoneGate />;
  return children;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Gate>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/confidentialite" element={<Suspense fallback={null}><Privacy /></Suspense>} />
            <Route path="/mentions-legales" element={<Suspense fallback={null}><LegalNotice /></Suspense>} />
            <Route path="/conditions" element={<Suspense fallback={null}><Conditions /></Suspense>} />
            <Route path="/auth/verify" element={<Verify />} />
            <Route path="/auth/google" element={<AuthGoogle />} />
            <Route
              path="/app"
              element={
                <RequireAuth>
                  <AppShell />
                </RequireAuth>
              }
            >
              <Route index element={<ParcoursPage />} />
              <Route path="quetes" element={<KeepQuery to="/app" />} />
              <Route path="jo" element={<KeepQuery to="/app" add="jo=1" />} />
              <Route path="fiche" element={<FichePage />} />
              <Route path="mots-cles" element={<Navigate to="/app/fiche#mots-cles" replace />} />
              <Route path="zones" element={<Navigate to="/app/fiche#zones" replace />} />
              <Route path="reglages" element={<SettingsPage />} />
            </Route>
            <Route
              path="/session"
              element={
                <RequireAuth>
                  <AppShell />
                </RequireAuth>
              }
            >
              <Route index element={<SessionPage />} />
            </Route>
            <Route path="/admin" element={<Suspense fallback={<div className="min-h-dvh bg-bg" />}><AdminPage /></Suspense>} />
            {!PROD_HOST && <Route path="/admin/apercus" element={<Suspense fallback={<div className="min-h-dvh bg-bg" />}><ApercusPage /></Suspense>} />}
            <Route path="/admin/utilisateurs/:id" element={<Suspense fallback={<div className="min-h-dvh bg-bg" />}><AdminPage /></Suspense>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Gate>
          <CookieBanner />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
